/**
 * useOnboardingBuild — the real work behind /welcome/building.
 *
 * The step used to run a five-row checklist on 3.4-second timers with no
 * backend behind it. This runs two genuinely distinct pieces of work and
 * reports each one as it actually resolves:
 *
 *   1. create   — the Genie itself (instructions, voice, greeting), plus its
 *                 activation pack, which the create call raises server-side.
 *   2. knowledge — reading the owner's website into the Genie's knowledge.
 *
 * Rules this hook keeps:
 *   - Never show progress for work that is not happening. A stage is `waiting`,
 *     `working`, `done`, `failed` or `skipped`, and nothing else.
 *   - Creation failing stops the flow. There is no Genie, so there is nothing
 *     honest to show downstream.
 *   - The website read failing does NOT stop the flow. The Genie exists and is
 *     useful; the step says so plainly and the owner can add knowledge later.
 *   - Runs exactly once per mount, even under React strict-mode double effects.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { useOnboarding } from "@/contexts/voice-onboarding/OnboardingContext";
import { OnboardingLaunchService, normaliseWebsite } from "@/services/voice-onboarding/OnboardingLaunchService";
import type { OnboardingBuildResult, OnboardingBuildStage, OnboardingBuildStageId } from "@/types/voice-vision/launch";

const initialStages = (website: string | undefined): OnboardingBuildStage[] => {
  const site = normaliseWebsite(website)?.replace(/^https?:\/\//, "").replace(/\/$/, "");
  return [
    {
      id: "create",
      label: "Writing your genie's instructions, voice and greeting",
      sub: "Who it is, what it helps with, and when to pass a caller to a person",
      state: "waiting",
    },
    {
      id: "knowledge",
      label: site ? `Reading ${site}` : "Reading your website",
      sub: site ? "Hours, services, pricing and FAQs into its knowledge" : "No website was given, so there is nothing to read",
      state: site ? "waiting" : "skipped",
    },
  ];
};

export const useOnboardingBuild = () => {
  const { user } = useAuth();
  const { draft, setDraft } = useOnboarding();

  const [stages, setStages] = useState<OnboardingBuildStage[]>(() => initialStages(draft.business.website));
  const [result, setResult] = useState<OnboardingBuildResult | null>(null);
  const [fatalError, setFatalError] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const started = useRef(false);

  const patchStage = useCallback(
    (id: OnboardingBuildStageId, patch: Partial<OnboardingBuildStage>) => {
      setStages((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
    },
    [],
  );

  const run = useCallback(async () => {
    setIsRunning(true);
    setFatalError(null);

    /* ── 1. Create the Genie. Fatal if it fails. ─────────────────────────── */
    patchStage("create", { state: "working" });

    let agent;
    try {
      agent = await OnboardingLaunchService.createGenie(draft);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "We could not build your genie just now.";
      patchStage("create", { state: "failed", sub: message });
      patchStage("knowledge", { state: "skipped", sub: "Nothing to add knowledge to yet" });
      setFatalError(message);
      setIsRunning(false);
      return;
    }

    const genieId = agent.id;
    const urlName = agent.page?.url_name ?? agent.url_name ?? null;

    patchStage("create", {
      state: "done",
      sub: `${agent.name} is live, with its share page and activation pack ready`,
    });

    // Activation funnel — `activation_pack_generated` and the chat
    // `channel_connected` are both emitted inside the create path now
    // (ActivationPackService.createPack and AgentProvisionService), so every
    // Genie-creation route is covered, not just `/welcome`. Nothing to emit
    // here.

    // Persist the real Genie immediately, so Meet, Test and Launch have it even
    // if the website read is still going or later fails.
    setDraft((d) => ({
      ...d,
      genieId,
      urlName: urlName ?? undefined,
      genieName: agent.name,
    }));

    /* ── 2. Read the website. Never fatal. ───────────────────────────────── */
    const site = normaliseWebsite(draft.business.website);
    let knowledge = { attempted: false, ok: false, pages: null as number | null, message: null as string | null };

    if (site && user?.id) {
      patchStage("knowledge", { state: "working" });
      knowledge = await OnboardingLaunchService.readWebsite(draft.business.website, user.id, genieId);

      if (knowledge.ok) {
        patchStage("knowledge", {
          state: "done",
          sub: knowledge.pages
            ? `Read ${knowledge.pages} page${knowledge.pages === 1 ? "" : "s"} into its knowledge`
            : "Your site is in its knowledge",
        });
      } else {
        patchStage("knowledge", {
          state: "failed",
          sub: `${knowledge.message ?? "We could not read that site."} Your genie still works. You can add knowledge any time.`,
        });
      }
    } else if (site && !user?.id) {
      // Signed-out is not a state this route should reach, but say so honestly
      // rather than silently skipping real work.
      patchStage("knowledge", {
        state: "failed",
        sub: "We could not read your website because we could not confirm your account.",
      });
      knowledge = {
        attempted: true,
        ok: false,
        pages: null,
        message: "Account could not be confirmed.",
      };
    }

    setDraft((d) => ({ ...d, built: true, knowledgeSummary: knowledge }));
    setResult({ genieId, urlName, genieName: agent.name, knowledge });
    setIsRunning(false);
  }, [draft, patchStage, setDraft, user?.id]);

  useEffect(() => {
    if (started.current) return;
    // Already built (a Back-then-Forward through the wizard) — do not create twice.
    if (draft.genieId) {
      started.current = true;
      setStages((prev) => prev.map((s) => (s.state === "waiting" ? { ...s, state: "done" } : s)));
      setResult({
        genieId: draft.genieId,
        urlName: draft.urlName ?? null,
        genieName: draft.genieName ?? "Your genie",
        knowledge: draft.knowledgeSummary ?? { attempted: false, ok: false, pages: null, message: null },
      });
      return;
    }
    started.current = true;
    void run();
    // `run` closes over the draft on purpose: the build uses the draft as it
    // stood when the step was entered, and must not re-fire as it updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retry = useCallback(() => {
    setStages(initialStages(draft.business.website));
    setResult(null);
    void run();
  }, [draft.business.website, run]);

  return {
    stages,
    result,
    fatalError,
    isRunning,
    /** True only when there is a real Genie to move on to. */
    isComplete: Boolean(result?.genieId),
    retry,
  };
};
